import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useMatchRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { AppIcon } from './ui/icon'
import { mdiAccount, mdiArrowLeft, mdiCalendar, mdiClose, mdiGraphOutline, mdiPencil, mdiTrashCanOutline } from '@mdi/js'
import { useExpense, useAsset, useUser, useDocument, useContract, useContracts, useUpdateExpense, useDeleteExpense, type UpdateExpenseRequest } from '../hooks/useQueries'
import { useInterventions } from '../hooks/useInterventions'
import { useApi } from '../api/hooks'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useFormatters } from '../lib/format'
import { useLocale } from '../contexts/LocaleContext'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { UserSelect } from './UserSelect'
import { AttachDocumentControl, type AttachDocumentControlLabels } from './documents/AttachDocumentControl'
import { DocumentPreviewCard } from './documents/DocumentPreviewCard'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { EntityTagsSection } from './tags/EntityTagsSection'
import { EmptyState } from './ui/empty-state'
import { SECTION_ICONS } from '../lib/section-icons'
import { ExpenseCategoryCard } from './expenses/ExpenseCategoryCard'
import { ExpenseRecoverableCard } from './expenses/ExpenseRecoverableCard'
import { ExpenseActivityCard } from './expenses/ExpenseActivityCard'
import { useCan } from '../contexts/PermissionsContext'

export function ExpensePage() {
  const { org, expenseUid } = useParams({ from: '/$org/expenses/$expenseUid' })
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { formatMoney } = useFormatters()
  const { request } = useApi()

  const isEditing = !!matchRoute({ to: '/$org/expenses/$expenseUid/edit' })
  const canEditFinance = useCan('finance.write')
  const canReadFinance = useCan('finance.read')

  const { data: expense, isLoading, error } = useExpense(org || 'demo', expenseUid || '')

  // Resolve display names for the related asset and supplier. Both hooks are
  // guarded on a valid uid, so they no-op until the expense has loaded and the
  // corresponding relation is set.
  const { data: asset } = useAsset(org || 'demo', expense?.asset_uid || '')
  const { data: supplier } = useUser(org || 'demo', expense?.supplier_user_uid || '')
  const { data: sourceDocument } = useDocument(org || 'demo', expense?.document_uid || '')
  const { data: relatedContract } = useContract(org || 'demo', expense?.contract_uid || '')
  const { data: contracts = [] } = useContracts(org || 'demo', { limit: 200 })
  const { data: interventions = [] } = useInterventions(org || 'demo', undefined, {})

  const updateMutation = useUpdateExpense(org || 'demo', expenseUid || '')
  const deleteMutation = useDeleteExpense(org || 'demo')

  // Edit form state
  const [label, setLabel] = useState('')
  const [amountHtEur, setAmountHtEur] = useState('')
  const [taxEur, setTaxEur] = useState('')
  const [amountTtcEur, setAmountTtcEur] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [expenseDate, setExpenseDate] = useState('')
  const [supplierUserUid, setSupplierUserUid] = useState('')
  const [assetUid, setAssetUid] = useState('')
  const [interventionUid, setInterventionUid] = useState('')
  const [contractUid, setContractUid] = useState('')
  const [documentUid, setDocumentUid] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  // Captured at selection time (UserSelect's onOptionSelect) so the combobox
  // shows the NEWLY picked supplier's name immediately, instead of the stale
  // label from the original useUser(expense.supplier_user_uid) fetch — which
  // only updates after save+refetch. See
  // specs/todos/2026-07-21-04-user-pickers-truncated-to-first-page.md.
  const [selectedSupplierOption, setSelectedSupplierOption] = useState<ApiSearchableSelectOption | null>(null)

  useEffect(() => {
    if (expense) {
      setLabel(expense.label || '')
      setAmountHtEur(expense.amount_without_tax != null ? String(expense.amount_without_tax / 100) : '')
      setTaxEur(expense.tax_amount != null ? String(expense.tax_amount / 100) : '')
      setAmountTtcEur(expense.amount != null ? String(expense.amount / 100) : '')
      setCurrency(expense.currency || 'EUR')
      // The ledger dates a line by accrual_date; expense_date is the legacy field.
      const date = expense.accrual_date || expense.expense_date
      setExpenseDate(date ? date.slice(0, 10) : '')
      setSupplierUserUid(expense.supplier_user_uid || '')
      setAssetUid(expense.asset_uid || '')
      setInterventionUid(expense.intervention_uid || '')
      setContractUid(expense.contract_uid || '')
      setDocumentUid(expense.document_uid || '')
      setSelectedSupplierOption(null)
    }
  }, [expense])

  const searchAssets = useCallback(
    async (query: string): Promise<ApiSearchableSelectOption[]> => {
      try {
        const queryParams: Record<string, string> = { limit: '50' }
        if (query) { queryParams.q = query; queryParams.sort = 'name' }
        const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
          params: { path: { orgCode: org || 'demo' }, query: queryParams },
        })
        const items = ((response as any)?.data || []) as any[]
        return items.map((a) => ({ value: a.uid, label: a.name || a.external_ids?.code || a.uid }))
      } catch (err) {
        console.error('Asset search error', err)
        return []
      }
    },
    [org, request],
  )

  const assetInitialOptions = useMemo<ApiSearchableSelectOption[]>(() => {
    if (assetUid && asset) return [{ value: assetUid, label: (asset as any).name || assetUid }]
    return []
  }, [assetUid, asset])
  const supplierInitialOptions = useMemo<ApiSearchableSelectOption[]>(() => {
    if (selectedSupplierOption && selectedSupplierOption.value && selectedSupplierOption.value === supplierUserUid) {
      return [selectedSupplierOption]
    }
    if (supplierUserUid && supplier) return [{ value: supplierUserUid, label: supplier.name || supplier.email || supplierUserUid }]
    return []
  }, [supplierUserUid, supplier, selectedSupplierOption])

  const documentControlLabels: AttachDocumentControlLabels = useMemo(() => ({
    attachExisting: t('detail.field_document_attach_existing'),
    upload: t('detail.field_document_upload'),
    uploading: t('detail.field_document_uploading'),
    pickDocumentTitle: t('detail.field_document_pick'),
    searchPlaceholder: t('detail.field_document_search'),
    noDocumentsFound: t('detail.field_document_not_found'),
    alreadyAttached: t('detail.field_document_already_attached'),
  }), [t])

  // Amounts are stored in integer cents — divide by 100 for display.
  const formatAmount = (cents: number | null | undefined, currencyCode?: string | null) => {
    if (cents == null) return t('detail.no_value')
    return formatMoney(cents, currencyCode)
  }

  // Expense dates are calendar days: read YYYY-MM-DD as a local day, not as
  // UTC midnight, or a reader west of Greenwich sees the day before.
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return t('detail.no_value')
    const [y, m, d] = dateString.slice(0, 10).split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(locale)
  }

  const toCents = (v: string): number | null => (v.trim() ? Math.round(parseFloat(v) * 100) : null)

  const handleEdit = () => {
    navigate({ to: '/$org/expenses/$expenseUid/edit', params: { org: org || 'demo', expenseUid: expenseUid || '' } })
  }

  const handleCancel = () => {
    navigate({ to: '/$org/expenses/$expenseUid', params: { org: org || 'demo', expenseUid: expenseUid || '' } })
  }

  const handleSave = async () => {
    if (!expense) return
    try {
      const body: UpdateExpenseRequest = {
        label: label.trim(),
        amount_without_tax: toCents(amountHtEur),
        tax_amount: toCents(taxEur),
        amount: toCents(amountTtcEur) ?? 0,
        currency: currency.trim() || 'EUR',
        // The date field is the accrual date. The legacy expense_date is left
        // as it is, and accrual_date cannot be cleared: omit it when emptied.
        accrual_date: expenseDate || undefined,
        supplier_user_uid: supplierUserUid || null,
        asset_uid: assetUid || undefined,
        intervention_uid: interventionUid || null,
        contract_uid: contractUid || null,
        document_uid: documentUid || null,
      }
      await updateMutation.mutateAsync(body)
      navigate({ to: '/$org/expenses/$expenseUid', params: { org: org || 'demo', expenseUid: expenseUid || '' } })
    } catch (err) {
      console.error('Failed to update expense:', err)
    }
  }

  const confirmDelete = async () => {
    if (!expense) return
    try {
      await deleteMutation.mutateAsync(expense.uid!)
      setShowDeleteDialog(false)
      window.location.href = `/${org}/expenses`
    } catch (err) {
      console.error('Failed to delete expense:', err)
    }
  }

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/expenses` },
    {
      title: expense
        ? (isEditing ? t('detail.breadcrumb_editing', { title: expense.label || t('detail.breadcrumb_details') }) : (expense.label || t('detail.breadcrumb_details')))
        : t('detail.breadcrumb_details'),
      path: isEditing ? undefined : `/${org}/expenses/${expenseUid}`,
    },
  ], [org, expense, isEditing, expenseUid, t])

  useBreadcrumbs(breadcrumbs)

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  // Fatal not-found/error state — the only place a Back button is allowed
  // per front/CLAUDE.md, since the breadcrumb above may not point at a
  // usable parent when the expense itself failed to load.
  if (error || !expense) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">{t('detail.error_title')}</h1>
          <p className="text-muted-foreground">
            {(error as any)?.title || (error as any)?.message || t('detail.error_not_found')}
          </p>
          <Link to="/$org/expenses" params={{ org: org || 'demo' }}>
            <Button className="mt-4" data-testid="expense-back-to-list">
              <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
              {t('detail.back_to_expenses')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const supplierName = supplier?.name || supplier?.email

  return (
    <div className="container mx-auto py-8 max-w-6xl" data-testid="expense-detail">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <AppIcon tone="muted" path={SECTION_ICONS.expenses} size="xl" />
              <h1 className="text-3xl font-bold text-foreground" data-testid="expense-title">
                {isEditing ? `${t('detail.editing_prefix')} ${expense.label || t('detail.no_value')}` : (expense.label || t('detail.no_value'))}
              </h1>
            </div>
            {!isEditing && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                <span className="text-2xl font-semibold text-foreground" data-testid="expense-amount-ttc">
                  {formatAmount(expense.amount, expense.currency)}
                </span>
                {(expense.accrual_date || expense.expense_date) && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <AppIcon path={mdiCalendar} size="sm" />
                    {formatDate(expense.accrual_date || expense.expense_date)}
                  </span>
                )}
              </div>
            )}
            {expense.uid && (
              <EntityTagsSection
                collapse={{ below: 'md', max: 2 }}
                org={org || 'demo'}
                entityType="expense"
                entityUid={expense.uid}
                className="flex flex-wrap items-center gap-1 mt-2"
              />
            )}
          </div>
          <div className="flex items-center space-x-2">
            {expense?.uid && (
              <Link
                to="/$org/graph"
                params={{ org: org || 'demo' }}
                search={{ focus: `exp@${expense.uid}`, expanded: undefined }}
              >
                <Button
                  variant="outline"
                  size="icon"
                  data-testid="view-in-graph-btn"
                  aria-label={t('graph:action.view_in_graph')}
                  title={t('graph:action.view_in_graph')}
                >
                  <AppIcon path={mdiGraphOutline} size="sm" />
                </Button>
              </Link>
            )}
            {!isEditing ? (
              <Button
                variant="outline"
                size="icon"
                onClick={handleEdit}
                data-testid="edit-expense"
                aria-label={t('detail.edit')}
                title={t('detail.edit')}
              >
                <AppIcon path={mdiPencil} size="sm" />
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="save-expense"
                  aria-label={t('detail.save')}
                  title={t('detail.save')}
                >
                  <AppIcon path={mdiPencil} size="sm" className="md:mr-2" />
                  <span className="hidden md:inline">{t('detail.save')}</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  data-testid="cancel-edit-expense"
                  aria-label={t('detail.cancel')}
                  title={t('detail.cancel')}
                >
                  <AppIcon path={mdiClose} size="sm" className="md:mr-2" />
                  <span className="hidden md:inline">{t('detail.cancel')}</span>
                </Button>
              </div>
            )}
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleteMutation.isPending}
              data-testid="delete-expense"
              aria-label={t('detail.delete')}
              title={t('detail.delete')}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AppIcon path={SECTION_ICONS.documents} size="md" />
                  {t('detail.section_details')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_label')}</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-label"
                        autoFocus
                      />
                    ) : (
                      <p className="text-muted-foreground">{expense.label || t('detail.no_value')}</p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_currency')}</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-currency"
                      />
                    ) : (
                      <p className="text-muted-foreground">{expense.currency || 'EUR'}</p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_amount_ht')}</h3>
                    {isEditing ? (
                      <input
                        type="number"
                        value={amountHtEur}
                        onChange={(e) => setAmountHtEur(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-amount-ht"
                      />
                    ) : (
                      <p className="text-muted-foreground" data-testid="expense-amount-ht">
                        {formatAmount(expense.amount_without_tax, expense.currency)}
                      </p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_tax')}</h3>
                    {isEditing ? (
                      <input
                        type="number"
                        value={taxEur}
                        onChange={(e) => setTaxEur(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-tax"
                      />
                    ) : (
                      <p className="text-muted-foreground" data-testid="expense-tax">
                        {formatAmount(expense.tax_amount, expense.currency)}
                      </p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_amount_ttc')}</h3>
                    {isEditing ? (
                      <input
                        type="number"
                        value={amountTtcEur}
                        onChange={(e) => setAmountTtcEur(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-amount-ttc"
                      />
                    ) : (
                      <p className="text-foreground font-medium">
                        {formatAmount(expense.amount, expense.currency)}
                      </p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AppIcon path={mdiCalendar} size="sm" />
                      {t('detail.field_date')}
                    </h3>
                    {isEditing ? (
                      <input
                        type="date"
                        value={expenseDate}
                        onChange={(e) => setExpenseDate(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-date"
                      />
                    ) : (
                      <p className="text-muted-foreground">{formatDate(expense.accrual_date || expense.expense_date)}</p>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_created')}</p>
                    <p className="text-sm">
                      {expense.created_at ? new Date(expense.created_at).toLocaleString(locale) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_updated')}</p>
                    <p className="text-sm">
                      {expense.updated_at ? new Date(expense.updated_at).toLocaleString(locale) : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {expense.uid && (
              <ExpenseCategoryCard org={org || 'demo'} expenseUid={expense.uid} canEdit={canEditFinance && !isEditing} />
            )}
            {expense.uid && (
              <ExpenseRecoverableCard org={org || 'demo'} expenseUid={expense.uid} canEdit={canEditFinance && !isEditing} />
            )}
            {expense.uid && canReadFinance && <ExpenseActivityCard org={org || 'demo'} expenseUid={expense.uid} />}
          </div>

          {/* Related entities */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('detail.section_relations')}</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-4" data-testid="expense-relations-edit">
                    <div>
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <AppIcon path={SECTION_ICONS.assets} size="sm" />
                        {t('detail.related_asset')}
                      </h3>
                      <ApiSearchableSelect
                        value={assetUid}
                        onValueChange={setAssetUid}
                        onSearch={searchAssets}
                        initialOptions={assetInitialOptions}
                        placeholder={t('detail.field_asset_placeholder')}
                        data-testid="expense-field-asset"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <AppIcon path={SECTION_ICONS.documents} size="sm" />
                        {t('detail.related_document')}
                      </h3>
                      {documentUid ? (
                        <div className="mb-2">
                          <DocumentPreviewCard
                            org={org || 'demo'}
                            documentUid={documentUid}
                            document={sourceDocument}
                            testidPrefix="expense-field-document-current"
                            onDetach={() => setDocumentUid('')}
                          />
                        </div>
                      ) : (
                        <EmptyState
                          variant="inline"
                          className="mb-2 py-3"
                          description={t('detail.field_document_none')}
                          data-testid="expense-field-document-empty"
                        />
                      )}
                      <AttachDocumentControl
                        org={org || 'demo'}
                        assetUid={assetUid || undefined}
                        testidPrefix="expense-document"
                        labels={documentControlLabels}
                        onUploaded={(uid) => setDocumentUid(uid)}
                        onSelect={(uid) => setDocumentUid(uid)}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <AppIcon path={SECTION_ICONS.contracts} size="sm" />
                        {t('detail.related_contract')}
                      </h3>
                      <select
                        value={contractUid}
                        onChange={(e) => setContractUid(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-contract"
                      >
                        <option value="">{t('detail.field_contract_none')}</option>
                        {contracts.map((c: any) => (
                          <option key={c.uid} value={c.uid}>{c.name || c.code || c.uid}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <AppIcon path={SECTION_ICONS.interventions} size="sm" />
                        {t('detail.related_intervention')}
                      </h3>
                      <select
                        value={interventionUid}
                        onChange={(e) => setInterventionUid(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="expense-field-intervention"
                      >
                        <option value="">{t('detail.field_intervention_none')}</option>
                        {interventions.map((iv: any) => (
                          <option key={iv.uid} value={iv.uid}>{iv.reference} — {iv.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <AppIcon path={mdiAccount} size="sm" />
                        {t('detail.related_supplier')}
                      </h3>
                      <UserSelect
                        org={org || 'demo'}
                        value={supplierUserUid}
                        onValueChange={setSupplierUserUid}
                        onOptionSelect={setSelectedSupplierOption}
                        initialOptions={supplierInitialOptions}
                        placeholder={t('detail.field_supplier_none')}
                        data-testid="expense-field-supplier"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="expense-relations">
                    {/* Asset */}
                    {expense.asset_uid && (
                      <div className="flex items-start gap-3">
                        <AppIcon path={SECTION_ICONS.assets} size="sm" className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{t('detail.related_asset')}</p>
                          <Link
                            to="/$org/assets/$assetUid"
                            params={{ org: org || 'demo', assetUid: expense.asset_uid }}
                            className="text-sm font-medium text-primary hover:underline truncate block"
                            data-testid="expense-asset-link"
                          >
                            {(asset as any)?.name || t('detail.asset_unknown')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Source document */}
                    {expense.document_uid && (
                      <div className="flex items-start gap-3">
                        <AppIcon path={SECTION_ICONS.documents} size="sm" className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{t('detail.related_document')}</p>
                          <Link
                            to="/$org/documents/$docUid"
                            params={{ org: org || 'demo', docUid: expense.document_uid }}
                            className="text-sm font-medium text-primary hover:underline truncate block"
                            data-testid="expense-document-link"
                          >
                            {sourceDocument?.name || t('detail.document_unknown')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Contract */}
                    {expense.contract_uid && (
                      <div className="flex items-start gap-3">
                        <AppIcon path={SECTION_ICONS.contracts} size="sm" className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{t('detail.related_contract')}</p>
                          <Link
                            to="/$org/contracts/$contractUid"
                            params={{ org: org || 'demo', contractUid: expense.contract_uid }}
                            className="text-sm font-medium text-primary hover:underline truncate block"
                            data-testid="expense-contract-link"
                          >
                            {relatedContract?.name || relatedContract?.code || t('detail.contract_unknown')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Intervention */}
                    {expense.intervention_uid && (
                      <div className="flex items-start gap-3">
                        <AppIcon path={SECTION_ICONS.interventions} size="sm" className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{t('detail.related_intervention')}</p>
                          <Link
                            to="/$org/interventions/$interventionUid"
                            params={{ org: org || 'demo', interventionUid: expense.intervention_uid }}
                            className="text-sm font-medium text-primary hover:underline truncate block"
                            data-testid="expense-intervention-link"
                          >
                            {t('detail.view_intervention')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Supplier */}
                    {expense.supplier_user_uid && (
                      <div className="flex items-start gap-3">
                        <AppIcon path={mdiAccount} size="sm" className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{t('detail.related_supplier')}</p>
                          <Link
                            to="/$org/users/$userUid"
                            params={{ org: org || 'demo', userUid: expense.supplier_user_uid }}
                            className="text-sm font-medium text-primary hover:underline truncate block"
                            data-testid="expense-supplier-link"
                          >
                            {supplierName || t('detail.supplier_unknown')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* No relations */}
                    {!expense.asset_uid &&
                      !expense.document_uid &&
                      !expense.contract_uid &&
                      !expense.intervention_uid &&
                      !expense.supplier_user_uid && (
                        <EmptyState
                          variant="inline"
                          className="py-3"
                          description={t('detail.related_none')}
                          data-testid="expense-relations-empty"
                        />
                      )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Delete confirmation — AlertDialog (never native confirm()). */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.delete_confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-expense-cancel">{t('detail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="delete-expense-confirm"
            >
              {t('detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
